import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ClientDashboard() {
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30');

  // Calculate date range based on selection
  const getDateRange = (range) => {
    const today = new Date();
    const endDate = today.toISOString().split('T')[0];
    let startDate;

    if (range === '7') {
      startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    } else if (range === '30') {
      startDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    } else if (range === '90') {
      startDate = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    }

    return { startDate, endDate };
  };

  // Fetch client info
  useEffect(() => {
    const fetchClientInfo = async () => {
      try {
        const response = await axios.get(`${API}/clients/me`);
        setClient(response.data);
      } catch (err) {
        console.error('Failed to fetch client:', err);
        setError('Failed to load client info');
        toast.error('Failed to load client info');
      }
    };

    if (user?.client_id) {
      fetchClientInfo();
    }
  }, [user?.client_id]);

  // Fetch metrics and summary
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!user?.client_id) return;

      try {
        setLoading(true);
        setError(null);

        const { startDate, endDate } = getDateRange(dateRange);

        // Fetch metrics
        const metricsRes = await axios.get(`${API}/clients/${user.client_id}/metrics`, {
          params: { start_date: startDate, end_date: endDate }
        });

        // Fetch summary
        const summaryRes = await axios.get(`${API}/clients/${user.client_id}/metrics/summary`, {
          params: { start_date: startDate, end_date: endDate }
        });

        // Format metrics for chart (ascending order for chart display)
        const formattedMetrics = (metricsRes.data || [])
          .reverse()
          .map(m => ({
            date: new Date(m.snapshot_date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }),
            fullDate: m.snapshot_date,
            spend: Math.round(m.spend_cents / 100 * 100) / 100,
            leads: m.leads,
            cpl: Math.round(m.cpl_cents / 100 * 100) / 100,
            roas: m.roas,
            impressions: m.impressions,
            clicks: m.clicks
          }));

        setMetrics(formattedMetrics);
        setSummary(summaryRes.data);
      } catch (err) {
        console.error('Failed to fetch metrics:', err);
        setError('Failed to load metrics');
        toast.error('Failed to load metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [user?.client_id, dateRange]);

  const SummaryCard = ({ title, value, unit = '' }) => (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6 col-span-1">
      <p className="text-white/60 text-sm font-medium mb-2">{title}</p>
      <p className="text-2xl font-bold text-white">
        {typeof value === 'number' && title.includes('$')
          ? `$${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : typeof value === 'number'
          ? value.toLocaleString('en-AU')
          : value}
        {unit && <span className="text-sm text-white/40 ml-1">{unit}</span>}
      </p>
    </div>
  );

  if (loading && !metrics.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-8 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
          <p className="text-white/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !metrics.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">{client?.name || 'Client Dashboard'}</h1>
            <p className="text-white/60 text-sm mt-1">Performance metrics and analytics</p>
          </div>

          {/* Date Range Selector */}
          <div className="flex gap-2 flex-wrap">
            {[
              { label: 'Last 7 days', value: '7' },
              { label: 'Last 30 days', value: '30' },
              { label: 'Last 90 days', value: '90' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setDateRange(option.value)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  dateRange === option.value
                    ? 'bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/30'
                    : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Spend"
            value={summary?.total_spend || 0}
            unit="AUD"
          />
          <SummaryCard
            title="Total Leads"
            value={summary?.total_leads || 0}
          />
          <SummaryCard
            title="Avg CPL"
            value={summary?.avg_cpl || 0}
            unit="AUD"
          />
          <SummaryCard
            title="Avg ROAS"
            value={(summary?.avg_roas || 0).toFixed(2)}
            unit="x"
          />
        </div>

        {/* Trend Chart */}
        {metrics.length > 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-bold text-white mb-6">30-Day Trend</h2>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart
                data={metrics}
                margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis
                  dataKey="date"
                  stroke="rgba(255,255,255,0.4)"
                  style={{ fontSize: '12px' }}
                  tick={{ fill: 'rgba(255,255,255,0.6)' }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="rgba(255,255,255,0.4)"
                  style={{ fontSize: '12px' }}
                  tick={{ fill: 'rgba(255,255,255,0.6)' }}
                  label={{ value: 'Spend (AUD)', angle: -90, position: 'insideLeft' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="rgba(212,168,75,0.6)"
                  style={{ fontSize: '12px' }}
                  tick={{ fill: 'rgba(212,168,75,0.6)' }}
                  label={{ value: 'Leads', angle: 90, position: 'insideRight' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(13, 13, 13, 0.9)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                  formatter={(value, name) => {
                    if (name === 'spend') return [`$${value.toFixed(2)}`, 'Spend'];
                    if (name === 'leads') return [value, 'Leads'];
                    return [value, name];
                  }}
                  labelFormatter={label => label}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                  formatter={() => ['Spend (AUD)', 'Leads']}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="spend"
                  stroke="#00d4aa"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={true}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="leads"
                  stroke="#D4A84B"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
            <p className="text-white/60">No data available for this period</p>
          </div>
        )}

        {/* Platform Breakdown */}
        {metrics.length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-bold text-white mb-6">Platform Performance</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-white/80 font-medium">Platform</th>
                    <th className="text-right py-3 px-4 text-white/80 font-medium">Spend</th>
                    <th className="text-right py-3 px-4 text-white/80 font-medium">Leads</th>
                    <th className="text-right py-3 px-4 text-white/80 font-medium">CPL</th>
                    <th className="text-right py-3 px-4 text-white/80 font-medium">ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Group metrics by data_source from original API response */}
                  {metrics.length > 0 && (
                    <tr className="border-b border-white/10 hover:bg-white/5 transition-colors">
                      <td className="py-3 px-4 text-white">Meta Ads</td>
                      <td className="text-right py-3 px-4 text-white/80">
                        ${summary?.total_spend?.toFixed(2) || '0.00'}
                      </td>
                      <td className="text-right py-3 px-4 text-white/80">
                        {summary?.total_leads || 0}
                      </td>
                      <td className="text-right py-3 px-4 text-white/80">
                        ${summary?.avg_cpl?.toFixed(2) || '0.00'}
                      </td>
                      <td className="text-right py-3 px-4 text-[#D4A84B] font-medium">
                        {summary?.avg_roas?.toFixed(2) || '0.00'}x
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientDashboard;
